import React, { useState, useMemo, useCallback } from 'react';
import { formatPrice } from '../utils/currency';
import './CompositeProductWizard.css';

// ─── CompositeCalculator (espelho da lógica do backend) ────────────────────────

function calcularDivisao(regra, selecoes) {
  const valores = selecoes.filter(s => s.quantidade > 0).map(s => s.valor);
  if (valores.length === 0) return 0;
  switch (regra) {
    case 'maior_valor':
      return Math.max(...valores);
    case 'media':
      return valores.reduce((a, b) => a + b, 0) / valores.length;
    case 'soma_proporcional':
      return valores.reduce((sum, v) => sum + (v / valores.length), 0);
    default:
      return Math.max(...valores);
  }
}

function calcularInclusos(etapa, selecoes) {
  const totalItens = selecoes.reduce((sum, s) => sum + s.quantidade, 0);
  const excedentes = Math.max(0, totalItens - etapa.itens_inclusos);
  return excedentes * parseFloat(etapa.valor_excedente || 0);
}

function calcularEtapa(etapa, selecoes) {
  if (!selecoes || selecoes.length === 0) return 0;
  const selecoesAtivas = selecoes.filter(s => s.quantidade > 0);
  if (selecoesAtivas.length === 0) return 0;

  if (etapa.regra_divisao && selecoesAtivas.length > 1) {
    return calcularDivisao(etapa.regra_divisao, selecoesAtivas);
  }
  if (etapa.itens_inclusos > 0) {
    return calcularInclusos(etapa, selecoesAtivas);
  }
  return selecoesAtivas.reduce((sum, sel) => sum + (parseFloat(sel.valor) * sel.quantidade), 0);
}

function calcularTotal(etapas, selecoesPorEtapa) {
  let total = 0;
  for (const etapa of etapas) {
    const selecoes = selecoesPorEtapa[etapa.id] || [];
    total += calcularEtapa(etapa, selecoes);
  }
  return Math.round(total * 100) / 100;
}

// ─── WizardStepIndicator ───────────────────────────────────────────────────────

function WizardStepIndicator({ etapas, currentStep, onStepClick }) {
  return (
    <div className="wizard-step-indicator">
      {etapas.map((etapa, idx) => (
        <div
          key={etapa.id}
          className={`wizard-step-dot-container ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
          onClick={() => idx < currentStep && onStepClick(idx)}
        >
          <div className="wizard-step-dot">
            {idx < currentStep ? '✓' : idx + 1}
          </div>
          <span className="wizard-step-label">{etapa.nome}</span>
          {idx < etapas.length - 1 && <div className="wizard-step-line" />}
        </div>
      ))}
    </div>
  );
}

// ─── StepContent ───────────────────────────────────────────────────────────────

function StepContent({ etapa, selecoes, onSelecaoChange }) {
  const isSingleSelect = etapa.max_selecoes === 1;
  const hasInclusos = etapa.itens_inclusos > 0;
  const totalSelecionado = selecoes.reduce((sum, s) => sum + s.quantidade, 0);

  const handleSingleSelect = useCallback((item) => {
    const novasSelecoes = [{ ...item, quantidade: 1 }];
    onSelecaoChange(etapa.id, novasSelecoes);
  }, [etapa.id, onSelecaoChange]);

  const handleIncrement = useCallback((item) => {
    if (etapa.max_selecoes && totalSelecionado >= etapa.max_selecoes) return;
    const existing = selecoes.find(s => s.id === item.id);
    let novasSelecoes;
    if (existing) {
      novasSelecoes = selecoes.map(s =>
        s.id === item.id ? { ...s, quantidade: s.quantidade + 1 } : s
      );
    } else {
      novasSelecoes = [...selecoes, { ...item, quantidade: 1 }];
    }
    onSelecaoChange(etapa.id, novasSelecoes);
  }, [etapa.id, etapa.max_selecoes, selecoes, totalSelecionado, onSelecaoChange]);

  const handleDecrement = useCallback((item) => {
    const novasSelecoes = selecoes
      .map(s => s.id === item.id ? { ...s, quantidade: s.quantidade - 1 } : s)
      .filter(s => s.quantidade > 0);
    onSelecaoChange(etapa.id, novasSelecoes);
  }, [etapa.id, selecoes, onSelecaoChange]);

  const getItemQuantidade = (itemId) => {
    const sel = selecoes.find(s => s.id === itemId);
    return sel ? sel.quantidade : 0;
  };

  const isItemSelected = (itemId) => getItemQuantidade(itemId) > 0;

  return (
    <div className="wizard-step-content">
      {/* Header da etapa */}
      <div className="wizard-step-header">
        <h5 className="wizard-step-title">
          {etapa.nome}
          {etapa.b_obrigatorio ? (
            <span className="wizard-badge-obrigatorio">OBRIGATÓRIO</span>
          ) : null}
        </h5>
        <p className="wizard-step-info">
          {etapa.min_selecoes > 0 && `Mín: ${etapa.min_selecoes} `}
          {etapa.max_selecoes && `Máx: ${etapa.max_selecoes}`}
        </p>
        {hasInclusos && (
          <div className="wizard-inclusos-counter">
            <span className="wizard-inclusos-text">
              {Math.min(totalSelecionado, etapa.itens_inclusos)} de {etapa.itens_inclusos} inclusos utilizados
            </span>
            {totalSelecionado > etapa.itens_inclusos && (
              <span className="wizard-excedente-info">
                Excedente: {formatPrice(etapa.valor_excedente)} cada
              </span>
            )}
          </div>
        )}
        <div className="wizard-step-counter">
          {totalSelecionado}/{etapa.max_selecoes || '∞'}
        </div>
      </div>

      {/* Lista de itens */}
      <div className="wizard-items-list">
        {etapa.itens && etapa.itens.filter(i => i.b_ativo !== 0 && i.b_ativo !== '0' && i.b_ativo !== false).map((item) => {
          const quantidade = getItemQuantidade(item.id);
          const selected = isItemSelected(item.id);

          return (
            <div
              key={`item_${etapa.id}_${item.id}`}
              className={`wizard-item ${selected ? 'selected' : ''}`}
              onClick={() => {
                if (isSingleSelect) {
                  handleSingleSelect(item);
                } else {
                  handleIncrement(item);
                }
              }}
            >
              <div className="wizard-item-left">
                {/* Indicador radio ou checkbox */}
                <div className={`wizard-item-indicator ${isSingleSelect ? 'radio' : 'checkbox'} ${selected ? 'active' : ''}`}>
                  {selected && <span className="wizard-check">✓</span>}
                </div>

                {/* Info do item */}
                <div className="wizard-item-info">
                  <div className="wizard-item-nome">{item.nome}</div>
                  <div className="wizard-item-valor">
                    {hasInclusos ? (
                      selected
                        ? <span className="wizard-valor-incluso">Incluso</span>
                        : <span className="wizard-valor-incluso">Incluso</span>
                    ) : etapa.regra_divisao ? (
                      <span className="wizard-valor-divisao">{formatPrice(item.valor)}</span>
                    ) : (
                      item.valor > 0
                        ? <span className="wizard-valor-normal">+ {formatPrice(item.valor)}</span>
                        : <span className="wizard-valor-gratis">Sem custo extra</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Controles de quantidade para multi-select */}
              {!isSingleSelect && (
                <div className="wizard-item-controls" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="wizard-qty-btn"
                    onClick={(e) => { e.stopPropagation(); handleDecrement(item); }}
                    disabled={quantidade === 0}
                  >
                    −
                  </button>
                  <span className="wizard-qty-value">{quantidade}</span>
                  <button
                    className="wizard-qty-btn"
                    onClick={(e) => { e.stopPropagation(); handleIncrement(item); }}
                    disabled={etapa.max_selecoes && totalSelecionado >= etapa.max_selecoes}
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── StepSummary ───────────────────────────────────────────────────────────────

function StepSummary({ etapas, selecoesPorEtapa }) {
  const total = useMemo(() => calcularTotal(etapas, selecoesPorEtapa), [etapas, selecoesPorEtapa]);

  return (
    <div className="wizard-summary-bar">
      <div className="wizard-summary-label">Total</div>
      <div className="wizard-summary-value">{formatPrice(total)}</div>
    </div>
  );
}

// ─── CompositeProductWizard (main) ─────────────────────────────────────────────

function CompositeProductWizard({ produto, onComplete, onClose }) {
  const etapas = produto.etapas_montagem || [];
  const [currentStep, setCurrentStep] = useState(0);
  const [selecoesPorEtapa, setSelecoesPorEtapa] = useState({});
  const [erroValidacao, setErroValidacao] = useState(null);

  const etapaAtual = etapas[currentStep];
  const isLastStep = currentStep === etapas.length - 1;
  const selecoesEtapaAtual = selecoesPorEtapa[etapaAtual?.id] || [];

  // Cálculo do total em tempo real
  const totalCalculado = useMemo(
    () => calcularTotal(etapas, selecoesPorEtapa),
    [etapas, selecoesPorEtapa]
  );

  // Verifica se a etapa atual está válida (sem bloquear)
  const etapaValida = useMemo(() => {
    if (!etapaAtual) return true;
    const totalSelecionado = selecoesEtapaAtual.reduce((sum, s) => sum + s.quantidade, 0);
    if (etapaAtual.b_obrigatorio && totalSelecionado < etapaAtual.min_selecoes) {
      return false;
    }
    return true;
  }, [etapaAtual, selecoesEtapaAtual]);

  // Atualizar seleções de uma etapa
  const handleSelecaoChange = useCallback((etapaId, novasSelecoes) => {
    setSelecoesPorEtapa(prev => ({
      ...prev,
      [etapaId]: novasSelecoes
    }));
    setErroValidacao(null);
  }, []);

  // Validação ao tentar avançar
  const validarEtapaAtual = useCallback(() => {
    if (!etapaAtual) return true;
    const totalSelecionado = selecoesEtapaAtual.reduce((sum, s) => sum + s.quantidade, 0);
    if (etapaAtual.b_obrigatorio && totalSelecionado < etapaAtual.min_selecoes) {
      const faltam = etapaAtual.min_selecoes - totalSelecionado;
      setErroValidacao(`Selecione mais ${faltam} ${faltam === 1 ? 'item' : 'itens'} em "${etapaAtual.nome}"`);
      return false;
    }
    setErroValidacao(null);
    return true;
  }, [etapaAtual, selecoesEtapaAtual]);

  // Navegar para próxima etapa
  const handleProximo = useCallback(() => {
    if (!validarEtapaAtual()) return;
    if (!isLastStep) {
      setCurrentStep(prev => prev + 1);
      setErroValidacao(null);
    }
  }, [validarEtapaAtual, isLastStep]);

  // Navegar para etapa anterior
  const handleVoltar = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setErroValidacao(null);
    }
  }, [currentStep]);

  // Clicar numa etapa no indicador
  const handleStepClick = useCallback((idx) => {
    setCurrentStep(idx);
    setErroValidacao(null);
  }, []);

  // Finalizar montagem
  const handleFinalizar = useCallback(() => {
    if (!validarEtapaAtual()) return;

    // Montar resultado no formato esperado pelo backend
    const etapasResult = etapas.map(etapa => {
      const selecoes = selecoesPorEtapa[etapa.id] || [];
      return {
        id_etapa: etapa.id,
        selecoes: selecoes
          .filter(s => s.quantidade > 0)
          .map(s => ({ id_item: s.id, quantidade: s.quantidade }))
      };
    }).filter(e => e.selecoes.length > 0);

    onComplete({
      valor: totalCalculado,
      etapas: etapasResult
    });
  }, [validarEtapaAtual, etapas, selecoesPorEtapa, totalCalculado, onComplete]);

  if (!etapas.length) return null;

  return (
    <div className="composite-wizard-overlay">
      <div className="composite-wizard-container">
        {/* Header */}
        <div className="composite-wizard-header">
          <button className="composite-wizard-close" onClick={onClose} aria-label="Fechar">×</button>
          <h4 className="composite-wizard-title">{produto.nome}</h4>
        </div>

        {/* Step Indicator */}
        <WizardStepIndicator
          etapas={etapas}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />

        {/* Content */}
        <div className="composite-wizard-body">
          {etapaAtual && (
            <StepContent
              etapa={etapaAtual}
              selecoes={selecoesEtapaAtual}
              onSelecaoChange={handleSelecaoChange}
            />
          )}
        </div>

        {/* Erro de validação */}
        {erroValidacao && (
          <div className="composite-wizard-error">
            {erroValidacao}
          </div>
        )}

        {/* Footer com resumo + navegação */}
        <div className="composite-wizard-footer">
          <StepSummary etapas={etapas} selecoesPorEtapa={selecoesPorEtapa} />
          <div className="composite-wizard-nav">
            {currentStep > 0 && (
              <button className="wizard-btn wizard-btn-back" onClick={handleVoltar}>
                Voltar
              </button>
            )}
            {!isLastStep ? (
              <button
                className={`wizard-btn wizard-btn-next ${!etapaValida ? 'disabled' : ''}`}
                onClick={handleProximo}
                disabled={!etapaValida}
              >
                Próximo
              </button>
            ) : (
              <button
                className={`wizard-btn wizard-btn-add ${!etapaValida ? 'disabled' : ''}`}
                onClick={handleFinalizar}
                disabled={!etapaValida}
              >
                Adicionar ao carrinho • {formatPrice(totalCalculado)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompositeProductWizard;
